import {
  useCallback, useEffect, useMemo, useRef,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { getGetAllJobsQueryKey, useGetJobStatus, useRunJobInEngine } from '@/common/api/endpoints/jobs/jobs';
import { useDialog } from '@/common/context/DialogContext';
import log from '@/common/utils/logger';
import { oralPoolJobKey, useModuleJobStore } from './useModuleJobStore';

interface UseOralExamPoolJobParams {
  moduleId: string;
  version: string;
  taskId: string;
}

interface UseOralExamPoolJobWatchItemParams {
  moduleId: string;
  version: string;
  taskId: string;
  jobId: string;
  tasksQueryKey: readonly unknown[];
}

interface OralExamPoolJobWatchersProps {
  moduleId: string | null | undefined;
  version: string | null | undefined;
  tasksQueryKey: readonly unknown[];
}

interface StoredJobStatus {
  id: string;
  status: string;
  currentStage?: string;
}

interface LaunchOralExamPoolJobArgs {
  questionCount: number;
  label: string;
}

const jobStatusQueryOptions = {
  retry: false,
  refetchInterval: (query: { state: { error?: unknown; data?: unknown } }) => {
    if (query.state.error) return false;
    const jobs = (query.state.data as { data?: { data?: StoredJobStatus[] } } | undefined)?.data?.data;
    const job = jobs?.[0];
    if (!job) return 5000;
    if (job.status === 'running' || job.status === 'pending') return 5000;
    return false;
  },
  select: (response: { data?: unknown }) => {
    const jobs = (response.data as { data?: StoredJobStatus[] } | undefined)?.data;
    return jobs?.[0];
  },
};

/**
 * One stored oral-exam pool job. Mounted per task so two Generates in the
 * same version are polled independently.
 *
 * `tasksQueryKey` is read from a ref: the lecturer module data hook builds a
 * new array each render, and treating that as an effect dep cancelled the
 * in-flight completion (store left set → form stuck on isGenerating).
 *
 * Cleanup never aborts clear/invalidate. A vanished job (404 / empty) still
 * refetches the task list — the generate may already have persisted.
 */
export const useOralExamPoolJobWatchItem = ({
  moduleId,
  version,
  taskId,
  jobId,
  tasksQueryKey,
}: UseOralExamPoolJobWatchItemParams) => {
  const { t } = useTranslation();
  const { showToastMessage } = useDialog();
  const queryClient = useQueryClient();
  const clearOralPoolJob = useModuleJobStore((state) => state.clearOralPoolJob);
  const tasksQueryKeyRef = useRef(tasksQueryKey);
  tasksQueryKeyRef.current = tasksQueryKey;
  const handledRef = useRef(false);

  const {
    data: trackedJob,
    isError: isTrackedJobError,
    isPending: isTrackedJobPending,
    isFetched: isTrackedJobFetched,
  } = useGetJobStatus(jobId, undefined, {
    query: {
      enabled: !!jobId,
      ...jobStatusQueryOptions,
    },
  });

  useEffect(() => {
    const vanished = isTrackedJobError
      || (isTrackedJobFetched && !isTrackedJobPending && !trackedJob);
    const terminal = trackedJob?.status === 'success' || trackedJob?.status === 'failed';

    if (!vanished && !terminal) return;
    if (handledRef.current) return;
    handledRef.current = true;

    const finish = async () => {
      try {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: tasksQueryKeyRef.current }),
          queryClient.invalidateQueries({ queryKey: getGetAllJobsQueryKey({}) }),
          queryClient.invalidateQueries({ queryKey: getGetAllJobsQueryKey({ completed: true }) }),
        ]);
        clearOralPoolJob(moduleId, version, taskId);
        if (vanished) return;
        if (trackedJob?.status === 'success') {
          showToastMessage('success', t('lecturer.oralExamConfig.messages.generateSuccess'));
        } else {
          showToastMessage(
            'error',
            trackedJob?.currentStage || t('lecturer.oralExamConfig.messages.generateFailed'),
          );
        }
      } catch (error) {
        handledRef.current = false;
        log.error(error);
        showToastMessage('error', t('lecturer.oralExamConfig.messages.generateFailed'));
      }
    };

    finish().catch((error) => {
      log.error(error);
    });
  }, [
    trackedJob,
    isTrackedJobError,
    isTrackedJobFetched,
    isTrackedJobPending,
    clearOralPoolJob,
    queryClient,
    moduleId,
    version,
    taskId,
    showToastMessage,
    t,
  ]);
};

function OralExamPoolJobWatchItemHost({
  moduleId,
  version,
  taskId,
  jobId,
  tasksQueryKey,
}: UseOralExamPoolJobWatchItemParams) {
  useOralExamPoolJobWatchItem({
    moduleId,
    version,
    taskId,
    jobId,
    tasksQueryKey,
  });
  return null;
}

/**
 * Module Editor host: one watch item per stored pool job for this version.
 */
export function OralExamPoolJobWatchers({
  moduleId,
  version,
  tasksQueryKey,
}: OralExamPoolJobWatchersProps) {
  const oralPoolJobs = useModuleJobStore((state) => state.oralPoolJobs);
  const entries = useMemo(() => {
    if (!moduleId || !version) return [];
    const prefix = `${moduleId}:${version}:`;
    return Object.entries(oralPoolJobs)
      .filter(([key]) => key.startsWith(prefix))
      .map(([key, jobId]) => ({ jobId, taskId: key.slice(prefix.length) }));
  }, [oralPoolJobs, moduleId, version]);

  if (!moduleId || !version) return null;

  return (
    <>
      {entries.map((entry) => (
        <OralExamPoolJobWatchItemHost
          key={`${entry.taskId}:${entry.jobId}`}
          moduleId={moduleId}
          version={version}
          taskId={entry.taskId}
          jobId={entry.jobId}
          tasksQueryKey={tasksQueryKey}
        />
      ))}
    </>
  );
}

/**
 * Enqueues oral-exam question pool generation as a bulk background job.
 * Completion / cache refresh lives in {@link OralExamPoolJobWatchers}.
 */
export const useOralExamPoolJob = ({
  moduleId,
  version,
  taskId,
}: UseOralExamPoolJobParams) => {
  const { t } = useTranslation();
  const { showToastMessage } = useDialog();
  const queryClient = useQueryClient();
  const runJobMutation = useRunJobInEngine();

  const storeKey = oralPoolJobKey(moduleId, version, taskId);
  const storedJobId = useModuleJobStore((state) => state.oralPoolJobs[storeKey]);
  const setOralPoolJob = useModuleJobStore((state) => state.setOralPoolJob);

  // Hold the generating UI until the Module Editor watcher clears the store
  // (after a successful refetch). Dropping this on `status === 'success'`
  // flashes the empty Add control over a stale pool.
  const isJobActive = !!storedJobId;

  const launch = useCallback(async ({ questionCount, label }: LaunchOralExamPoolJobArgs) => {
    if (isJobActive) return;
    try {
      const response = await runJobMutation.mutateAsync({
        data: {
          type: 'bulk',
          label,
          params: {
            operations: ['generate_oral_exam_pool'],
            entityIds: [taskId],
            options: {
              generate_oral_exam_pool: {
                module_id: moduleId,
                version,
                save_result: true,
                regenerate: true,
                question_count: questionCount,
              },
            },
          },
        },
      });

      const jobId = (response?.data?.data as { jobId?: string } | undefined)?.jobId;
      if (!jobId) {
        showToastMessage('error', t('lecturer.oralExamConfig.messages.generateFailed'));
        return;
      }

      setOralPoolJob(moduleId, version, taskId, jobId);
      queryClient.invalidateQueries({ queryKey: getGetAllJobsQueryKey({}) });
      showToastMessage('info', t('lecturer.oralExamConfig.messages.generateStarted'));
    } catch (error) {
      log.error(error);
      showToastMessage('error', t('lecturer.oralExamConfig.messages.generateFailed'));
    }
  }, [
    isJobActive,
    runJobMutation,
    taskId,
    moduleId,
    version,
    setOralPoolJob,
    queryClient,
    showToastMessage,
    t,
  ]);

  return {
    launch,
    isJobActive,
    isLaunching: runJobMutation.isPending,
  };
};

export default useOralExamPoolJob;
